import type { Exercise } from '../exercise-types';

/** Chest, front/side delts and triceps. */
export const PUSH_EXERCISES: Exercise[] = [
  // ---------------------------------------------------------------- chest ---
  {
    id: 'barbell_bench_press',
    name: 'Barbell bench press',
    aka: ['Flat bench'],
    slots: ['chest_primary'],
    pattern: 'horizontal_press',
    primary: ['chest'],
    secondary: ['triceps', 'delts_front'],
    equipment: ['barbell', 'flat_bench', 'power_rack'],
    kind: 'compound',
    loadType: 'barbell',
    difficulty: 2,
    quality: 10,
    defaultSets: 4,
    repRange: [5, 8],
    restSeconds: 180,
    incrementKg: 2.5,
    setup: [
      'Lie back with your eyes directly under the bar, feet flat and driving into the floor.',
      'Pinch your shoulder blades together and down, and keep them pinned there the whole set.',
      'Grip roughly 1.5× shoulder width, wrists stacked straight over your forearms.',
    ],
    cues: [
      'Lower to the bottom of your sternum, not your throat — elbows around 45–60° from your body.',
      'Touch the chest under control, then drive the bar back in a slight arc towards your face.',
      'Keep your upper back tight against the bench; only the bar moves.',
    ],
    mistakes: ['Flaring elbows straight out to 90°', 'Bouncing the bar off the ribcage'],
    variations: ['dumbbell_bench_press', 'smith_bench_press', 'chest_press_machine'],
    imagery: {
      start: 'lying flat on a bench, arms fully extended holding a barbell above the chest, elbows locked',
      end: 'same bench, barbell lowered until it touches the lower chest, elbows bent and tucked at about 45 degrees',
    },
  },
  {
    id: 'dumbbell_bench_press',
    name: 'Dumbbell bench press',
    slots: ['chest_primary', 'chest_secondary'],
    pattern: 'horizontal_press',
    primary: ['chest'],
    secondary: ['triceps', 'delts_front'],
    equipment: ['dumbbells', 'flat_bench'],
    kind: 'compound',
    loadType: 'dumbbell',
    difficulty: 2,
    quality: 9,
    defaultSets: 4,
    repRange: [6, 10],
    restSeconds: 150,
    incrementKg: 2,
    setup: [
      'Sit on the end of the bench with the dumbbells on your thighs.',
      'Kick them up one at a time as you lie back, shoulder blades pulled together.',
      'Start with the bells just outside your chest, palms facing forward.',
    ],
    cues: [
      'Press up and slightly inward so the bells finish close but never clang together.',
      'Let the dumbbells sink a touch deeper than a bar could — that extra range is the whole point.',
      'To finish, curl your knees up and sit forward rather than dropping them behind you.',
    ],
    variations: ['barbell_bench_press', 'incline_dumbbell_press', 'pec_deck_fly'],
    imagery: {
      start: 'lying flat on a bench holding two dumbbells at arms length above the chest, palms facing forward',
      end: 'dumbbells lowered to chest level beside the ribcage, elbows bent below the torso line',
    },
  },
  {
    id: 'incline_barbell_press',
    name: 'Incline barbell press',
    slots: ['chest_primary', 'chest_secondary'],
    pattern: 'incline_press',
    primary: ['chest', 'delts_front'],
    secondary: ['triceps'],
    equipment: ['barbell', 'adjustable_bench', 'power_rack'],
    kind: 'compound',
    loadType: 'barbell',
    difficulty: 2,
    quality: 9,
    defaultSets: 4,
    repRange: [6, 10],
    restSeconds: 180,
    incrementKg: 2.5,
    setup: [
      'Set the bench to 30° — steeper than that turns it into a shoulder press.',
      'Sit back with shoulder blades retracted, feet planted.',
      'Unrack with straight arms over your upper chest.',
    ],
    cues: [
      'Touch just below the collarbone, at the top of the chest.',
      'Keep the elbows moderately tucked; the incline already loads the front delt plenty.',
    ],
    mistakes: ['Setting the bench too steep', 'Letting the hips pop off the bench'],
    variations: ['incline_dumbbell_press', 'hammer_incline_press'],
    imagery: {
      start: 'lying on a bench set at 30 degree incline, barbell held at arms length over the upper chest',
      end: 'barbell lowered to touch the upper chest just below the collarbone on the inclined bench',
    },
  },
  {
    id: 'incline_dumbbell_press',
    name: 'Incline dumbbell press',
    slots: ['chest_secondary', 'chest_primary'],
    pattern: 'incline_press',
    primary: ['chest', 'delts_front'],
    secondary: ['triceps'],
    equipment: ['dumbbells', 'adjustable_bench'],
    kind: 'compound',
    loadType: 'dumbbell',
    difficulty: 1,
    quality: 9,
    defaultSets: 3,
    repRange: [8, 12],
    restSeconds: 120,
    incrementKg: 2,
    setup: [
      'Bench at 30°, dumbbells resting on your thighs.',
      'Kick them into place as you lie back and set your shoulder blades.',
    ],
    cues: [
      'Stretch at the bottom until you feel the upper chest load, then press up and in.',
      'Wrists stay stacked over the elbows throughout.',
    ],
    variations: ['incline_barbell_press', 'hammer_incline_press', 'low_to_high_cable_fly'],
    imagery: {
      start: 'seated on a 30 degree incline bench, two dumbbells pressed to full extension above the upper chest',
      end: 'dumbbells lowered to the sides of the upper chest on an incline bench, deep stretch position',
    },
  },
  {
    id: 'chest_press_machine',
    name: 'Chest press machine',
    slots: ['chest_primary', 'chest_secondary'],
    pattern: 'horizontal_press',
    primary: ['chest'],
    secondary: ['triceps', 'delts_front'],
    equipment: ['chest_press_machine'],
    kind: 'compound',
    loadType: 'machine',
    difficulty: 1,
    quality: 8,
    defaultSets: 3,
    repRange: [8, 12],
    restSeconds: 120,
    incrementKg: 5,
    setup: [
      'Set the seat so the handles line up with the middle of your chest, not your shoulders.',
      'Back flat against the pad, feet planted.',
    ],
    cues: [
      'Push through the base of your palm and squeeze the chest at full extension without locking hard.',
      'Control the negative — the machine makes it easy to just drop the weight.',
    ],
    variations: ['barbell_bench_press', 'dumbbell_bench_press', 'pec_deck_fly'],
    imagery: {
      start: 'seated at a chest press machine, hands on the handles at chest level, arms bent, chest stretched',
      end: 'seated at a chest press machine with arms fully extended forward, handles pushed away from the body',
    },
  },
  {
    id: 'smith_bench_press',
    name: 'Smith machine bench press',
    slots: ['chest_primary', 'chest_secondary'],
    pattern: 'horizontal_press',
    primary: ['chest'],
    secondary: ['triceps', 'delts_front'],
    equipment: ['smith_machine', 'flat_bench'],
    kind: 'compound',
    loadType: 'barbell',
    difficulty: 1,
    quality: 7,
    defaultSets: 3,
    repRange: [8, 12],
    restSeconds: 150,
    incrementKg: 2.5,
    setup: [
      'Slide the bench in so the bar tracks down to your mid-chest.',
      'Set the safety catches just below your chest height.',
    ],
    cues: [
      'The fixed bar path means you can push closer to failure safely — use that.',
      'Rotate the bar to unhook at the top, not mid-set.',
    ],
    variations: ['barbell_bench_press', 'chest_press_machine'],
    imagery: {
      start: 'lying on a bench under a smith machine, hands on the fixed bar at arms length above the chest',
      end: 'smith machine bar lowered to touch the mid chest while lying on the bench',
    },
  },
  {
    id: 'hammer_incline_press',
    name: 'Plate-loaded incline press',
    aka: ['Hammer Strength incline'],
    slots: ['chest_secondary'],
    pattern: 'incline_press',
    primary: ['chest', 'delts_front'],
    secondary: ['triceps'],
    equipment: ['incline_press_machine'],
    kind: 'compound',
    loadType: 'machine',
    difficulty: 1,
    quality: 8,
    defaultSets: 3,
    repRange: [8, 12],
    restSeconds: 120,
    incrementKg: 5,
    setup: [
      'Seat height so the handles sit at upper-chest level.',
      'Load plates evenly on both arms.',
    ],
    cues: [
      'Press one arm at a time occasionally — the independent arms let you fix a strength imbalance.',
      'Squeeze for a beat at the top.',
    ],
    variations: ['incline_dumbbell_press', 'incline_barbell_press'],
    imagery: {
      start: 'seated at a plate loaded incline press machine, hands gripping handles at upper chest, arms bent',
      end: 'arms extended forward and slightly upward at a plate loaded incline press machine',
    },
  },
  {
    id: 'weighted_dips',
    name: 'Chest dips',
    slots: ['chest_secondary', 'triceps_primary'],
    pattern: 'horizontal_press',
    primary: ['chest', 'triceps'],
    secondary: ['delts_front'],
    equipment: ['dip_station'],
    kind: 'compound',
    loadType: 'bodyweight',
    difficulty: 2,
    quality: 9,
    defaultSets: 3,
    repRange: [6, 12],
    restSeconds: 150,
    incrementKg: 2.5,
    setup: [
      'Grip the parallel bars and press up to a straight-arm support.',
      'Lean your torso forward about 30° and cross your ankles behind you.',
    ],
    cues: [
      'The forward lean is what makes it a chest exercise — stay upright and it becomes triceps.',
      'Descend until your upper arms are roughly parallel to the floor, then press back up.',
      'Add load with a dip belt once you can do 12 clean reps.',
    ],
    mistakes: ['Dropping too deep and straining the shoulder', 'Shrugging up at the bottom'],
    variations: ['machine_dip', 'close_grip_bench_press', 'decline_dumbbell_press'],
    imagery: {
      start: 'supported at the top of parallel dip bars, arms straight, torso leaning forward, ankles crossed',
      end: 'lowered between parallel dip bars, upper arms parallel to the floor, torso leaning forward',
    },
  },
  {
    id: 'decline_dumbbell_press',
    name: 'Decline dumbbell press',
    slots: ['chest_secondary'],
    pattern: 'horizontal_press',
    primary: ['chest'],
    secondary: ['triceps'],
    equipment: ['dumbbells', 'decline_bench'],
    kind: 'compound',
    loadType: 'dumbbell',
    difficulty: 2,
    quality: 7,
    defaultSets: 3,
    repRange: [8, 12],
    restSeconds: 120,
    incrementKg: 2,
    setup: [
      'Hook your legs in and lie back on the decline bench.',
      'Have the dumbbells passed to you or kick them up carefully.',
    ],
    cues: [
      'Lower to the lower chest, press straight up.',
      'Keep it moderate weight — getting out from under a decline is awkward.',
    ],
    variations: ['dumbbell_bench_press', 'weighted_dips'],
    imagery: {
      start: 'lying on a decline bench, legs hooked, dumbbells extended above the lower chest',
      end: 'dumbbells lowered to the sides of the lower chest on a decline bench',
    },
  },
  {
    id: 'push_up',
    name: 'Push-up',
    slots: ['chest_secondary', 'finisher'],
    pattern: 'horizontal_press',
    primary: ['chest'],
    secondary: ['triceps', 'delts_front', 'abs'],
    equipment: ['bodyweight'],
    kind: 'compound',
    loadType: 'bodyweight',
    difficulty: 1,
    quality: 6,
    defaultSets: 3,
    repRange: [12, 25],
    restSeconds: 90,
    incrementKg: 0,
    setup: ['Hands slightly wider than shoulders, body in one straight line from head to heels.'],
    cues: [
      'Squeeze glutes and brace your abs so your hips do not sag.',
      'Elbows back at ~45°, chest to within a fist of the floor.',
    ],
    variations: ['weighted_dips', 'chest_press_machine'],
    imagery: {
      start: 'top of a push-up, arms straight, body in a rigid straight line from head to heels, side view',
      end: 'bottom of a push-up, chest close to the floor, elbows bent at 45 degrees, body still straight',
    },
  },
  {
    id: 'cable_fly_mid',
    name: 'Cable fly',
    slots: ['chest_isolation'],
    pattern: 'isolation',
    primary: ['chest'],
    secondary: ['delts_front'],
    equipment: ['cable_crossover', 'single_handle'],
    kind: 'isolation',
    loadType: 'cable',
    difficulty: 1,
    quality: 9,
    defaultSets: 3,
    repRange: [12, 15],
    restSeconds: 75,
    incrementKg: 2.5,
    setup: [
      'Set both pulleys to shoulder height and take a handle in each hand.',
      'Step forward into a split stance with a slight forward lean.',
    ],
    cues: [
      'Keep a fixed, slightly bent elbow — think of hugging a barrel, not pressing.',
      'Bring the handles together and hold the squeeze for a full second.',
      'Cables keep tension at the stretched position where dumbbells lose it.',
    ],
    variations: ['pec_deck_fly', 'dumbbell_fly', 'low_to_high_cable_fly'],
    imagery: {
      start: 'standing between two cable towers in a split stance, arms wide open at shoulder height, chest stretched',
      end: 'arms brought together in front of the chest holding cable handles, elbows slightly bent, chest squeezed',
    },
  },
  {
    id: 'low_to_high_cable_fly',
    name: 'Low-to-high cable fly',
    slots: ['chest_isolation'],
    pattern: 'isolation',
    primary: ['chest'],
    secondary: ['delts_front'],
    equipment: ['cable_crossover', 'single_handle'],
    kind: 'isolation',
    loadType: 'cable',
    difficulty: 1,
    quality: 8,
    defaultSets: 3,
    repRange: [12, 15],
    restSeconds: 75,
    incrementKg: 2.5,
    setup: ['Both pulleys at the lowest setting, one handle per hand, split stance.'],
    cues: [
      'Sweep up and in, finishing with your hands around collarbone height.',
      'Biases the upper chest — good pairing with a flat press.',
    ],
    variations: ['cable_fly_mid', 'incline_dumbbell_press'],
    imagery: {
      start: 'standing in a split stance between low cable pulleys, arms down and wide at hip level',
      end: 'arms swept upward and together in front of the collarbones holding cable handles',
    },
  },
  {
    id: 'pec_deck_fly',
    name: 'Pec deck',
    aka: ['Butterfly machine'],
    slots: ['chest_isolation'],
    pattern: 'isolation',
    primary: ['chest'],
    secondary: [],
    equipment: ['pec_deck'],
    kind: 'isolation',
    loadType: 'machine',
    difficulty: 1,
    quality: 8,
    defaultSets: 3,
    repRange: [12, 15],
    restSeconds: 75,
    incrementKg: 5,
    setup: [
      'Seat height so the handles sit at chest level with your elbows slightly below shoulders.',
      'Back flat on the pad.',
    ],
    cues: [
      'Drive with the elbows, not the hands.',
      'Pause for a second at full contraction, control the stretch on the way back.',
    ],
    variations: ['cable_fly_mid', 'dumbbell_fly'],
    imagery: {
      start: 'seated at a pec deck machine with arms spread wide against the pads, chest stretched open',
      end: 'seated at a pec deck machine with the pads brought together in front of the chest',
    },
  },
  {
    id: 'dumbbell_fly',
    name: 'Dumbbell fly',
    slots: ['chest_isolation'],
    pattern: 'isolation',
    primary: ['chest'],
    secondary: [],
    equipment: ['dumbbells', 'flat_bench'],
    kind: 'isolation',
    loadType: 'dumbbell',
    difficulty: 2,
    quality: 7,
    defaultSets: 3,
    repRange: [12, 15],
    restSeconds: 75,
    incrementKg: 2,
    setup: ['Lie flat holding two light dumbbells above your chest, palms facing each other.'],
    cues: [
      'Lock a soft elbow bend and keep it — the angle should not change.',
      'Open until you feel a stretch across the chest, then hug back up.',
    ],
    mistakes: ['Going too heavy and turning it into a sloppy press'],
    variations: ['cable_fly_mid', 'pec_deck_fly'],
    imagery: {
      start: 'lying on a flat bench, two dumbbells held together above the chest, palms facing each other',
      end: 'lying on a flat bench, arms opened wide out to the sides with a slight elbow bend, deep chest stretch',
    },
  },

  // --------------------------------------------------------------- delts ----
  {
    id: 'overhead_press',
    name: 'Standing overhead press',
    aka: ['Military press', 'OHP'],
    slots: ['delts_press'],
    pattern: 'vertical_press',
    primary: ['delts_front', 'delts_side'],
    secondary: ['triceps', 'abs'],
    equipment: ['barbell', 'power_rack'],
    kind: 'compound',
    loadType: 'barbell',
    difficulty: 3,
    quality: 10,
    defaultSets: 4,
    repRange: [5, 8],
    restSeconds: 180,
    incrementKg: 2.5,
    setup: [
      'Bar in the rack at upper-chest height; grip just outside shoulder width.',
      'Unrack onto your front delts, elbows slightly in front of the bar.',
      'Feet hip width, glutes and abs braced hard.',
    ],
    cues: [
      'Pull your head back out of the way, press up, then push your head "through the window" at the top.',
      'Finish with the bar over the middle of your feet, biceps by your ears.',
      'No leg drive — if your knees bend it has become a push press.',
    ],
    mistakes: ['Leaning back through the lower spine instead of bracing the abs'],
    variations: ['seated_dumbbell_press', 'machine_shoulder_press', 'landmine_press'],
    imagery: {
      start: 'standing with a barbell racked at shoulder height on the front delts, elbows forward, braced',
      end: 'standing with a barbell locked out fully overhead, arms straight, bar over the midfoot, side view',
    },
  },
  {
    id: 'seated_dumbbell_press',
    name: 'Seated dumbbell shoulder press',
    slots: ['delts_press'],
    pattern: 'vertical_press',
    primary: ['delts_front', 'delts_side'],
    secondary: ['triceps'],
    equipment: ['dumbbells', 'adjustable_bench'],
    kind: 'compound',
    loadType: 'dumbbell',
    difficulty: 1,
    quality: 9,
    defaultSets: 3,
    repRange: [8, 12],
    restSeconds: 120,
    incrementKg: 2,
    setup: [
      'Set the bench upright or one notch back from vertical.',
      'Kick the dumbbells up to shoulder height, palms forward.',
    ],
    cues: [
      'Press up and slightly in; stop just short of the bells touching.',
      'Lower until your elbows are level with your shoulders.',
    ],
    variations: ['overhead_press', 'arnold_press', 'machine_shoulder_press'],
    imagery: {
      start: 'seated on an upright bench holding two dumbbells at shoulder height, palms facing forward',
      end: 'seated on an upright bench with two dumbbells pressed overhead, arms fully extended',
    },
  },
  {
    id: 'machine_shoulder_press',
    name: 'Machine shoulder press',
    slots: ['delts_press'],
    pattern: 'vertical_press',
    primary: ['delts_front', 'delts_side'],
    secondary: ['triceps'],
    equipment: ['shoulder_press_machine'],
    kind: 'compound',
    loadType: 'machine',
    difficulty: 1,
    quality: 7,
    defaultSets: 3,
    repRange: [10, 14],
    restSeconds: 90,
    incrementKg: 5,
    setup: ['Seat so the handles start at roughly ear height.'],
    cues: ['Press smoothly to just short of lockout, keeping constant tension.'],
    variations: ['seated_dumbbell_press', 'overhead_press'],
    imagery: {
      start: 'seated at a shoulder press machine, hands on handles at ear height, elbows bent',
      end: 'seated at a shoulder press machine with arms extended overhead',
    },
  },
  {
    id: 'arnold_press',
    name: 'Arnold press',
    slots: ['delts_press'],
    pattern: 'vertical_press',
    primary: ['delts_front', 'delts_side'],
    secondary: ['triceps'],
    equipment: ['dumbbells', 'adjustable_bench'],
    kind: 'compound',
    loadType: 'dumbbell',
    difficulty: 2,
    quality: 7,
    defaultSets: 3,
    repRange: [10, 12],
    restSeconds: 90,
    incrementKg: 2,
    setup: ['Seated upright, dumbbells at chin height with palms facing you.'],
    cues: [
      'Rotate the palms outward as you press, so they face forward at the top.',
      'Reverse the rotation on the way down. Lighter weight than a normal press.',
    ],
    variations: ['seated_dumbbell_press', 'overhead_press'],
    imagery: {
      start: 'seated holding two dumbbells at chin height with palms facing towards the face, elbows tucked in front',
      end: 'seated with dumbbells pressed overhead, palms rotated to face forward, arms extended',
    },
  },
  {
    id: 'landmine_press',
    name: 'Half-kneeling landmine press',
    slots: ['delts_press'],
    pattern: 'vertical_press',
    primary: ['delts_front'],
    secondary: ['triceps', 'abs', 'chest'],
    equipment: ['landmine', 'barbell'],
    kind: 'compound',
    loadType: 'barbell',
    difficulty: 2,
    quality: 7,
    unilateral: true,
    defaultSets: 3,
    repRange: [8, 12],
    restSeconds: 90,
    incrementKg: 2.5,
    setup: [
      'Barbell in the landmine sleeve, kneel on the same-side knee as the pressing arm.',
      'Hold the bar end at your shoulder.',
    ],
    cues: [
      'Press up and forward along the bar’s natural arc.',
      'Great option if straight overhead pressing bothers your shoulder.',
    ],
    variations: ['overhead_press', 'seated_dumbbell_press'],
    imagery: {
      start: 'half kneeling on one knee holding the end of a landmine barbell at the shoulder',
      end: 'half kneeling with the landmine barbell pressed up and forward, arm extended at an angle',
    },
  },
  {
    id: 'dumbbell_lateral_raise',
    name: 'Dumbbell lateral raise',
    slots: ['delts_side'],
    pattern: 'isolation',
    primary: ['delts_side'],
    secondary: ['traps'],
    equipment: ['dumbbells'],
    kind: 'isolation',
    loadType: 'dumbbell',
    difficulty: 1,
    quality: 9,
    defaultSets: 4,
    repRange: [12, 20],
    restSeconds: 60,
    incrementKg: 1,
    setup: ['Stand with light dumbbells at your sides, a small bend in the elbows.'],
    cues: [
      'Lead with the elbows, raise to shoulder height — no higher.',
      'Tilt the pinky slightly up, like pouring a jug.',
      'Lower slowly over 2–3 seconds. This is the one lift where ego costs you everything.',
    ],
    mistakes: ['Swinging with the hips', 'Shrugging the traps up to do the work'],
    variations: ['cable_lateral_raise', 'machine_lateral_raise', 'lean_away_lateral_raise'],
    imagery: {
      start: 'standing with two light dumbbells hanging at the sides of the body, slight elbow bend',
      end: 'standing with arms raised out to the sides to shoulder height holding dumbbells, elbows slightly bent',
    },
  },
  {
    id: 'cable_lateral_raise',
    name: 'Cable lateral raise',
    slots: ['delts_side'],
    pattern: 'isolation',
    primary: ['delts_side'],
    secondary: [],
    equipment: ['cable_station', 'single_handle'],
    kind: 'isolation',
    loadType: 'cable',
    difficulty: 1,
    quality: 9,
    unilateral: true,
    defaultSets: 3,
    repRange: [12, 20],
    restSeconds: 60,
    incrementKg: 2.5,
    setup: [
      'Pulley at the lowest setting, handle in the hand furthest from the machine.',
      'Stand side-on, cable passing in front of your body.',
    ],
    cues: [
      'Constant tension from the very bottom — the reason this beats dumbbells for side delts.',
      'Raise to shoulder height, pause, lower under control.',
    ],
    variations: ['dumbbell_lateral_raise', 'machine_lateral_raise'],
    imagery: {
      start: 'standing side on to a low cable pulley, holding the handle with the far hand across the body at hip level',
      end: 'arm raised out to the side to shoulder height against a low cable, side view',
    },
  },
  {
    id: 'machine_lateral_raise',
    name: 'Machine lateral raise',
    slots: ['delts_side'],
    pattern: 'isolation',
    primary: ['delts_side'],
    secondary: [],
    equipment: ['lateral_raise_machine'],
    kind: 'isolation',
    loadType: 'machine',
    difficulty: 1,
    quality: 8,
    defaultSets: 3,
    repRange: [12, 20],
    restSeconds: 60,
    incrementKg: 2.5,
    setup: ['Seat height so the pivot lines up with your shoulder joint.'],
    cues: ['Drive the pads out and up with the elbows, pause at the top.'],
    variations: ['dumbbell_lateral_raise', 'cable_lateral_raise'],
    imagery: {
      start: 'seated at a lateral raise machine with arms down against the pads at the sides',
      end: 'seated at a lateral raise machine with arms raised out to shoulder height against the pads',
    },
  },
  {
    id: 'lean_away_lateral_raise',
    name: 'Lean-away lateral raise',
    slots: ['delts_side'],
    pattern: 'isolation',
    primary: ['delts_side'],
    secondary: [],
    equipment: ['dumbbells', 'power_rack'],
    kind: 'isolation',
    loadType: 'dumbbell',
    difficulty: 2,
    quality: 8,
    unilateral: true,
    defaultSets: 3,
    repRange: [12, 18],
    restSeconds: 60,
    incrementKg: 1,
    setup: [
      'Hold an upright with one hand and lean away so your body is tilted.',
      'Dumbbell in the free hand, hanging across your body.',
    ],
    cues: [
      'The lean puts the side delt under tension at the very bottom of the rep.',
      'Raise to shoulder height without twisting your torso back.',
    ],
    variations: ['dumbbell_lateral_raise', 'cable_lateral_raise'],
    imagery: {
      start: 'holding a squat rack upright with one hand and leaning away, dumbbell hanging in the free hand',
      end: 'leaning away from a rack upright with the free arm raised out to shoulder height holding a dumbbell',
    },
  },
  {
    id: 'upright_row',
    name: 'EZ-bar upright row',
    slots: ['delts_side', 'traps'],
    pattern: 'isolation',
    primary: ['delts_side', 'traps'],
    secondary: ['biceps'],
    equipment: ['ez_bar'],
    kind: 'compound',
    loadType: 'barbell',
    difficulty: 2,
    quality: 6,
    defaultSets: 3,
    repRange: [10, 15],
    restSeconds: 75,
    incrementKg: 2.5,
    setup: ['Grip the EZ bar at roughly shoulder width, bar resting against your thighs.'],
    cues: [
      'Pull the elbows up and out, stopping when the upper arms reach shoulder height.',
      'Stop there — pulling higher jams the shoulder. Skip it entirely if it pinches.',
    ],
    mistakes: ['Going too narrow and too high'],
    variations: ['dumbbell_lateral_raise', 'dumbbell_shrug'],
    imagery: {
      start: 'standing holding an EZ curl bar at arms length against the thighs, shoulder width grip',
      end: 'standing having pulled an EZ bar up to chest height with elbows flared out at shoulder level',
    },
  },
  {
    id: 'reverse_pec_deck',
    name: 'Reverse pec deck',
    aka: ['Rear delt machine'],
    slots: ['delts_rear'],
    pattern: 'isolation',
    primary: ['delts_rear'],
    secondary: ['upper_back'],
    equipment: ['rear_delt_machine'],
    kind: 'isolation',
    loadType: 'machine',
    difficulty: 1,
    quality: 9,
    defaultSets: 3,
    repRange: [12, 20],
    restSeconds: 60,
    incrementKg: 2.5,
    setup: ['Chest against the pad, handles set so your arms start straight out in front at shoulder height.'],
    cues: [
      'Sweep the arms out and back in a wide arc, elbows nearly straight.',
      'Think about pulling with the back of the shoulder, not squeezing the shoulder blades.',
    ],
    variations: ['face_pull', 'bent_over_reverse_fly', 'cable_rear_delt_fly'],
    imagery: {
      start: 'seated chest against the pad of a reverse pec deck, arms straight out in front holding the handles',
      end: 'seated at a reverse pec deck with arms swept wide out to the sides and back, shoulder height',
    },
  },
  {
    id: 'face_pull',
    name: 'Cable face pull',
    slots: ['delts_rear', 'traps'],
    pattern: 'horizontal_pull',
    primary: ['delts_rear', 'upper_back'],
    secondary: ['traps'],
    equipment: ['cable_station', 'rope_attachment'],
    kind: 'isolation',
    loadType: 'cable',
    difficulty: 1,
    quality: 9,
    defaultSets: 3,
    repRange: [15, 20],
    restSeconds: 60,
    incrementKg: 2.5,
    setup: ['Rope attached at roughly forehead height, step back until the stack lifts slightly.'],
    cues: [
      'Pull the rope towards your forehead, splitting your hands apart as you go.',
      'Finish with your knuckles beside your ears and your elbows high.',
      'The best single thing you can do for shoulder health if you bench a lot.',
    ],
    variations: ['reverse_pec_deck', 'bent_over_reverse_fly'],
    imagery: {
      start: 'standing facing a high cable pulley holding a rope with both hands, arms extended forward at head height',
      end: 'rope pulled apart towards the forehead, elbows high and flared out wide beside the head',
    },
  },
  {
    id: 'bent_over_reverse_fly',
    name: 'Bent-over reverse fly',
    slots: ['delts_rear'],
    pattern: 'isolation',
    primary: ['delts_rear'],
    secondary: ['upper_back'],
    equipment: ['dumbbells'],
    kind: 'isolation',
    loadType: 'dumbbell',
    difficulty: 2,
    quality: 7,
    defaultSets: 3,
    repRange: [15, 20],
    restSeconds: 60,
    incrementKg: 1,
    setup: ['Hinge forward to about 45°, light dumbbells hanging beneath your chest.'],
    cues: [
      'Sweep the arms out to the sides with soft elbows.',
      'Keep your neck neutral and your back flat. Go light — rear delts are small.',
    ],
    variations: ['reverse_pec_deck', 'face_pull'],
    imagery: {
      start: 'standing hinged forward at 45 degrees with two light dumbbells hanging below the chest, arms straight',
      end: 'hinged forward with arms swept out wide to the sides holding dumbbells, shoulder blades open',
    },
  },
  {
    id: 'cable_rear_delt_fly',
    name: 'Cable rear delt fly',
    slots: ['delts_rear'],
    pattern: 'isolation',
    primary: ['delts_rear'],
    secondary: ['upper_back'],
    equipment: ['cable_crossover', 'single_handle'],
    kind: 'isolation',
    loadType: 'cable',
    difficulty: 2,
    quality: 8,
    defaultSets: 3,
    repRange: [15, 20],
    restSeconds: 60,
    incrementKg: 2.5,
    setup: [
      'Both pulleys at shoulder height, cross the cables and take the opposite handle in each hand.',
      'Stand tall in the middle.',
    ],
    cues: ['Pull the arms apart and back in a wide arc, finishing in a T shape.'],
    variations: ['reverse_pec_deck', 'face_pull'],
    imagery: {
      start: 'standing between two cable towers with arms crossed in front of the chest holding opposite handles',
      end: 'standing with arms pulled wide apart into a T shape holding crossed cable handles',
    },
  },

  // -------------------------------------------------------------- triceps ---
  {
    id: 'close_grip_bench_press',
    name: 'Close-grip bench press',
    slots: ['triceps_primary'],
    pattern: 'horizontal_press',
    primary: ['triceps'],
    secondary: ['chest', 'delts_front'],
    equipment: ['barbell', 'flat_bench', 'power_rack'],
    kind: 'compound',
    loadType: 'barbell',
    difficulty: 2,
    quality: 9,
    defaultSets: 4,
    repRange: [6, 10],
    restSeconds: 150,
    incrementKg: 2.5,
    setup: [
      'Grip at roughly shoulder width — not narrower, that only wrecks your wrists.',
      'Shoulder blades set, feet planted.',
    ],
    cues: [
      'Tuck the elbows to about 30° and lower to the lower chest.',
      'Drive up thinking "push the floor away with your triceps".',
    ],
    variations: ['weighted_dips', 'skull_crusher', 'machine_dip'],
    imagery: {
      start: 'lying on a flat bench holding a barbell at arms length with a narrow shoulder width grip',
      end: 'barbell lowered to the lower chest with elbows tucked close to the ribs, narrow grip',
    },
  },
  {
    id: 'skull_crusher',
    name: 'EZ-bar skull crusher',
    aka: ['Lying triceps extension'],
    slots: ['triceps_primary', 'triceps_secondary'],
    pattern: 'isolation',
    primary: ['triceps'],
    secondary: [],
    equipment: ['ez_bar', 'flat_bench'],
    kind: 'isolation',
    loadType: 'barbell',
    difficulty: 2,
    quality: 9,
    defaultSets: 3,
    repRange: [10, 14],
    restSeconds: 90,
    incrementKg: 2.5,
    setup: ['Lie flat holding an EZ bar over your chest with a narrow, angled grip.'],
    cues: [
      'Let the elbows drift slightly back as you lower — aim the bar behind your head, not at your face.',
      'That slight angle keeps tension on the long head at the stretch.',
    ],
    mistakes: ['Flaring the elbows wide', 'Letting the upper arms swing'],
    variations: ['overhead_cable_extension', 'close_grip_bench_press'],
    imagery: {
      start: 'lying on a flat bench holding an EZ bar at arms length above the chest, elbows locked',
      end: 'EZ bar lowered behind the head while lying on a bench, elbows bent and pointing up and slightly back',
    },
  },
  {
    id: 'machine_dip',
    name: 'Triceps dip machine',
    slots: ['triceps_primary'],
    pattern: 'horizontal_press',
    primary: ['triceps'],
    secondary: ['chest'],
    equipment: ['triceps_machine'],
    kind: 'compound',
    loadType: 'machine',
    difficulty: 1,
    quality: 7,
    defaultSets: 3,
    repRange: [10, 14],
    restSeconds: 90,
    incrementKg: 5,
    setup: ['Seat set so the handles sit beside your lower chest at the start.'],
    cues: ['Stay upright to keep it on the triceps, press down to near lockout.'],
    variations: ['weighted_dips', 'cable_pushdown_rope'],
    imagery: {
      start: 'seated upright at a triceps dip machine, hands on handles at chest height, elbows bent',
      end: 'seated at a triceps dip machine with arms pressed down to near full extension at the sides',
    },
  },
  {
    id: 'cable_pushdown_rope',
    name: 'Rope pushdown',
    slots: ['triceps_secondary'],
    pattern: 'isolation',
    primary: ['triceps'],
    secondary: [],
    equipment: ['cable_station', 'rope_attachment'],
    kind: 'isolation',
    loadType: 'cable',
    difficulty: 1,
    quality: 9,
    defaultSets: 3,
    repRange: [12, 15],
    restSeconds: 60,
    incrementKg: 2.5,
    setup: ['Rope on a high pulley, elbows pinned to your sides, slight forward lean.'],
    cues: [
      'Only the forearms move — upper arms stay glued in place.',
      'Split the rope apart at the bottom and squeeze.',
    ],
    mistakes: ['Letting the elbows drift forward and turning it into a press'],
    variations: ['cable_pushdown_bar', 'overhead_cable_extension', 'machine_dip'],
    imagery: {
      start: 'standing at a high cable holding a rope with elbows bent at 90 degrees pinned to the sides',
      end: 'rope pushed down to full arm extension with the rope ends split apart near the thighs',
    },
  },
  {
    id: 'cable_pushdown_bar',
    name: 'Straight-bar pushdown',
    slots: ['triceps_secondary'],
    pattern: 'isolation',
    primary: ['triceps'],
    secondary: [],
    equipment: ['cable_station', 'straight_bar_attachment'],
    kind: 'isolation',
    loadType: 'cable',
    difficulty: 1,
    quality: 8,
    defaultSets: 3,
    repRange: [12, 15],
    restSeconds: 60,
    incrementKg: 2.5,
    setup: ['Straight or V-bar on a high pulley, overhand grip at shoulder width.'],
    cues: ['Elbows locked to your ribs, push down until the arms are straight, control back to 90°.'],
    variations: ['cable_pushdown_rope', 'machine_dip'],
    imagery: {
      start: 'standing at a high cable holding a straight bar attachment with elbows bent at 90 degrees',
      end: 'straight bar pushed down to full arm extension against the thighs, elbows locked at the sides',
    },
  },
  {
    id: 'overhead_cable_extension',
    name: 'Overhead cable triceps extension',
    slots: ['triceps_secondary'],
    pattern: 'isolation',
    primary: ['triceps'],
    secondary: [],
    equipment: ['cable_station', 'rope_attachment'],
    kind: 'isolation',
    loadType: 'cable',
    difficulty: 2,
    quality: 9,
    defaultSets: 3,
    repRange: [12, 15],
    restSeconds: 60,
    incrementKg: 2.5,
    setup: [
      'Rope on a low-to-mid pulley, turn away from the machine and take it overhead.',
      'Step into a split stance with a slight forward lean.',
    ],
    cues: [
      'Elbows point forward and stay there; stretch deep behind your head.',
      'The overhead position loads the long head, which pushdowns barely touch.',
    ],
    variations: ['overhead_dumbbell_extension', 'skull_crusher'],
    imagery: {
      start: 'standing in a split stance facing away from a cable, rope held behind the head with elbows bent overhead',
      end: 'arms extended fully overhead and forward holding a cable rope, elbows locked out',
    },
  },
  {
    id: 'overhead_dumbbell_extension',
    name: 'Overhead dumbbell extension',
    slots: ['triceps_secondary'],
    pattern: 'isolation',
    primary: ['triceps'],
    secondary: [],
    equipment: ['dumbbells'],
    kind: 'isolation',
    loadType: 'dumbbell',
    difficulty: 1,
    quality: 7,
    defaultSets: 3,
    repRange: [12, 15],
    restSeconds: 60,
    incrementKg: 2,
    setup: ['Hold one dumbbell with both hands, cupping the top plate, pressed overhead.'],
    cues: ['Lower behind the head keeping the elbows narrow, then extend back up.'],
    variations: ['overhead_cable_extension', 'skull_crusher'],
    imagery: {
      start: 'standing holding a single dumbbell with both hands extended overhead, arms straight',
      end: 'dumbbell lowered behind the head with elbows bent and pointing upward, narrow',
    },
  },
  {
    id: 'triceps_kickback',
    name: 'Cable triceps kickback',
    slots: ['triceps_secondary'],
    pattern: 'isolation',
    primary: ['triceps'],
    secondary: [],
    equipment: ['cable_station', 'single_handle'],
    kind: 'isolation',
    loadType: 'cable',
    difficulty: 1,
    quality: 6,
    unilateral: true,
    defaultSets: 3,
    repRange: [12, 18],
    restSeconds: 45,
    incrementKg: 2.5,
    setup: ['Low pulley, hinge forward, upper arm parallel to your torso and locked there.'],
    cues: ['Extend from the elbow only and squeeze hard at the top.'],
    variations: ['cable_pushdown_rope', 'overhead_cable_extension'],
    imagery: {
      start: 'hinged forward holding a low cable handle with the elbow high and bent at 90 degrees beside the torso',
      end: 'arm extended straight back behind the body holding a cable handle, triceps contracted',
    },
  },
];
